import { isLicensePlate } from "@edaz/services/validators/isLicensePlate";
import range from "@edaz/services/validators/range";
import { bound } from "@frui.ts/helpers";
import { ConductorAllChildrenActive, Router } from "@frui.ts/screens";
import { validate } from "@frui.ts/validation";
import { action, computed, observable, reaction } from "mobx";
import AppRouter from "services/appRouter";
import { scrollToElement, scrollToFirstError } from "utils/scrollTo";
import * as types from "viewModels/common/types";
import LocalizationService from "services/localizationService";
import BatchOrderItemViewModel from "./batchOrderItemViewModel";
import SummaryViewModel from "./summaryMultiEshopViewModel";
import OutageService from "../../services/outageService";
import { PaymentResultFailureOrigin, PaymentResultFailureType } from "../../models/paymentResult";
import EnumService from "services/enumService";
import EventBus, { paymentValidSinceInvalid } from "services/eventBus";
import ChargeOrderService from "services/chargeOrderService";
import { ChargeOrderStatus } from "models/chargeOrder";
import ChargeOrderItem from "models/chargeOrderItem";
import { isMobile } from "react-device-detect";

export const BatchOrderViewModelNavigationName = "batch";

@Router.registerRoute({ name: Router.Self, route: BatchOrderViewModelNavigationName })
export default class BatchOrderViewModel extends ConductorAllChildrenActive<BatchOrderItemViewModel> {
  @observable errorMessage?: types.ErrorMessage;
  @observable paymentResultFailureType?: PaymentResultFailureType;
  @observable paymentResultFailureOrigin?: PaymentResultFailureOrigin;
  @observable invalidValidSince = false;

  summaryViewModel: SummaryViewModel;
  @observable formElement?: HTMLFormElement;
  private unsubscription: Array<() => void> = [];

  tg = (code: string) => this.localizationService.translateGeneral(`multiEshop.${code}`);

  constructor(
    public localizationService: LocalizationService,
    public enumService: EnumService,
    public chargeOrderService: ChargeOrderService,
    private outageService: OutageService,
    private batchOrderItemViewModelFactory: ReturnType<typeof BatchOrderItemViewModel.Factory>,
    private summaryViewModelFactory: ReturnType<typeof SummaryViewModel.Factory>,
    private router: AppRouter,
    private eventBus: EventBus
  ) {
    super();
    this.navigationName = BatchOrderViewModelNavigationName;
    this.summaryViewModel = this.summaryViewModelFactory(false, false, this.onContinue);
    this.eventBus.subscribe(paymentValidSinceInvalid, () => this.paymentValidSinceInvalid());
  }

  @bound
  async addOrderItem() {
    const chargeOrderItem = this.chargeOrderService.addChargeOrderItem();
    await this.enumService.lazyValues("charge_validity_rules");

    this.children.push(this.batchOrderItemViewModelFactory(chargeOrderItem, this.reset));
  }

  @bound
  removeOrderItem(chargeOrderItem: ChargeOrderItem) {
    const childIndex = this.children.findIndex(children => children.chargeOrderItem === chargeOrderItem);
    if (~childIndex) {
      this.children.splice(childIndex, 1);
    }
    this.chargeOrderService.removeChargeOrderItem(chargeOrderItem);
    this.chargeOrderService.chargeOrderItems.forEach(item => (item._isModified = true));
    this.chargeOrderService.persistChargeOrder();
  }

  protected async onActivate() {
    if (isMobile && this.stepper) {
      scrollToElement(this.stepper);
    }
    this.children.clear();
    this.chargeOrderService.chargeOrderItems.forEach(item =>
      this.children.push(this.batchOrderItemViewModelFactory(item, this.reset))
    );

    if (this.chargeOrderService.chargeOrderItems.length === 0) {
      this.addOrderItem();
    }
    if (this.chargeOrderService.chargeOrder._origin !== "multiple") {
      this.reset();
    }

    this.errorMessage = undefined;
    switch (this.chargeOrderService.chargeOrder._status) {
      case ChargeOrderStatus.PaidBankTransfer:
      case ChargeOrderStatus.Paid:
      case ChargeOrderStatus.Deferred:
        this.reset();
        break;
      case ChargeOrderStatus.Failed:
        this.chargeOrderService.reopenOrder();
        break;
    }

    this.unsubscription.push(
      reaction(
        () => this.chargeOrderService.areOrderItemsValid,
        areOrderItemsValid => {
          if (areOrderItemsValid && this.errorMessage === "invalidData") {
            this.errorMessage = undefined;
          }
        }
      )
    );
    this.invalidValidSince && this.validateAll();
    this.chargeOrderService.chargeOrder._origin = "multiple";
    this.chargeOrderService.persistChargeOrder();
    return super.onActivate();
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    this.unsubscription.forEach(unsubscribe => unsubscribe());
    this.unsubscription = [];
    return super.onDeactivate(isClosing);
  }

  @computed
  get stepper() {
    return window.document.getElementById("stepper");
  }

  @bound
  public onContinue() {
    this.chargeOrderService.chargeOrder.charges = this.children.map(children => children.chargeOrderItem);
    this.chargeOrderService.filterEmptyChargeOrderCharges();
    this.children.splice(
      0,
      this.children.length,
      ...this.children.filter(children => this.chargeOrderService.chargeOrderItems.includes(children.chargeOrderItem))
    );

    if (!this.validateAll()) {
      return;
    }
    this.chargeOrderService.chargeOrder._origin = "multiple";

    this.chargeOrderService.persistStoredChargeOrder(this.chargeOrderService.chargeOrder);

    scrollToElement(this.formElement);
    this.router.navigate("recap");
  }

  @action.bound
  paymentValidSinceInvalid() {
    this.errorMessage = "invalidData";
    this.invalidValidSince = true;
    this.onActivate();
  }

  @action.bound
  closePaymentError() {
    this.paymentResultFailureType = undefined;
    this.paymentResultFailureOrigin = undefined;
  }

  @action.bound
  reset() {
    this.chargeOrderService.reset();
    this.children.clear();
    this.chargeOrderService.chargeOrder._status = ChargeOrderStatus.Open;
    this.chargeOrderService.chargeOrderItems.forEach(item =>
      this.children.push(this.batchOrderItemViewModelFactory(item, this.reset))
    );
  }

  @computed
  get showPaymentErrorMessage(): boolean {
    return !!(this.paymentResultFailureType && this.paymentResultFailureOrigin);
  }

  @computed
  get paymentErrorTitle(): string {
    return this.localizationService.translateGeneral(
      `eshop.payment.status.failure.messages.${this.paymentResultFailureOrigin}.${this.paymentResultFailureType}.title`
    );
  }

  @computed
  get paymentErrorDescription(): string {
    return this.localizationService.translateGeneral(
      `eshop.payment.status.failure.messages.${this.paymentResultFailureOrigin}.${this.paymentResultFailureType}.description`
    );
  }

  @computed
  get showPaymentErrorLink(): boolean {
    return (
      this.paymentResultFailureOrigin == PaymentResultFailureOrigin.Init &&
      this.paymentResultFailureType == PaymentResultFailureType.AlreadyConfirmed
    );
  }

  @action.bound
  validateAll() {
    this.errorMessage = undefined;

    const chargeOrderItems = this.chargeOrderService.chargeOrder.charges;
    if (chargeOrderItems === undefined || chargeOrderItems.length === 0) return false;
    const orderItemsIsValid = chargeOrderItems.reduce((acc, item) => {
      return validate(item) && acc;
    }, true);

    const chargeOrderItemsHasValidLicensePlates = chargeOrderItems.reduce(
      (summary: boolean, item: ChargeOrderItem) =>
        summary &&
        item.licensePlates.reduce(
          (summary: boolean, licensePlate: string) =>
            summary && isLicensePlate(licensePlate).isValid && range(licensePlate, { min: 1, max: 10 }).isValid,
          true
        ),
      true
    );

    const chargeOrderItemsHasOverlays = this.children.reduce(
      (summary: boolean, itemVM) => summary || itemVM.licensePlatesHasOverlay,
      false
    );
    const isAllValid: boolean = !chargeOrderItemsHasOverlays && chargeOrderItemsHasValidLicensePlates && orderItemsIsValid;

    if (!isAllValid) {
      this.errorMessage = "invalidData";
      scrollToFirstError(this.formElement);
    }

    return isAllValid;
  }
}
